import unescapedText from 'I18n/utils/unescapedText';
import Small from 'UIComponents/elements/Small';
import UIIcon from 'UIComponents/icon/UIIcon';
import InlineFeedback from 'ai-components-ui-library/components/feedback/InlineFeedback';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { BreezeLoadingDot } from 'ai-components-ui-library/components/loading/BreezeLoadingDot';
import { BREEZE_TOOLTIP_DELAY } from 'chatspot-core/constants/Delays';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useState } from 'react';
import SimpleMessageSection from '../../conversational/SimpleMessageSection';
import SimpleMessageSectionDisabled from '../../conversational/SimpleMessageSectionDisabled';
import CopilotPreviewHeader from './CopilotPreviewHeader';
import SummaryTimestamp from './SummaryTimestamp';
import BreezeRefreshButton from './breeze/BreezeRefreshButton';
import { ExpandableCaret } from './expandable/ExpandableCaret';
import { useIsDisabledByAccessControls } from './hooks/useIsDisabledByAccessControls';
import { getCopyButtonText } from './utils/CopyButton';
import { useShouldShowActions } from '../../../hooks/useShouldShowActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CopilotPreviewInnerExpandable = ({
  children,
  hasAIBadge,
  onRefresh,
  onPositiveFeedback,
  onNegativeFeedback,
  onQuickActionClick,
  previewTitle,
  response,
  usageTracker,
  usageTrackerAppLocationKey,
  overrideDisabled,
  defaultExpanded,
  maxHeight,
  onExpand,
  isLoading,
  isStreaming
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const {
    isDisabledByAccessControls
  } = useIsDisabledByAccessControls();
  const copyMessage = getCopyButtonText(response);
  const isDisabled = (overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.isDisabled) || isDisabledByAccessControls;
  const shouldShowActions = useShouldShowActions();
  const hasResponse = response && response.sections && response.sections.length > 0;
  const renderHeader = () => {
    if (hasResponse || isLoading || isStreaming) {
      return /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        marginBlockEnd: "100",
        marginBlockStart: "-200",
        marginInlineStart: "-200",
        overflow: "hidden",
        flexWrap: "nowrap",
        gap: "100",
        children: [/*#__PURE__*/_jsx(Flex, {
          flexGrow: 1,
          flexShrink: 1,
          justifySelf: "stretch",
          children: /*#__PURE__*/_jsx(Clickable, {
            onClick: () => {
              setIsExpanded(!isExpanded);
              onExpand(!isExpanded);
            },
            alignItems: "center",
            cursor: "pointer",
            flexBasis: "100%",
            children: /*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              gap: "100",
              flexBasis: "100%",
              children: [/*#__PURE__*/_jsx(ExpandableCaret, {
                open: Boolean(isExpanded)
              }), /*#__PURE__*/_jsx(UIIcon, {
                name: "breezeSingleStar",
                size: 16
              }), /*#__PURE__*/_jsx(CopilotPreviewHeader, {
                previewTitle: previewTitle
              })]
            })
          })
        }), hasAIBadge && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          gap: "100",
          flexShrink: 0,
          children: /*#__PURE__*/_jsx(AIBadge, {})
        })]
      });
    }
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      marginBlockEnd: "100",
      onClick: () => setIsExpanded(!isExpanded),
      flexBasis: "100%",
      overflow: "hidden",
      gap: "100",
      children: [/*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "100",
        flexGrow: 0,
        flexShrink: 1,
        children: [/*#__PURE__*/_jsx(UIIcon, {
          name: "breezeSingleStar",
          size: 16
        }), /*#__PURE__*/_jsx(CopilotPreviewHeader, {
          previewTitle: previewTitle
        })]
      }), hasAIBadge && /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        gap: "100",
        flexGrow: 0,
        flexShrink: 0,
        children: /*#__PURE__*/_jsx(AIBadge, {})
      })]
    });
  };
  if (isLoading) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderHeader(), /*#__PURE__*/_jsxs(Flex, {
        marginBlockStart: "400",
        marginInline: "-300",
        children: [/*#__PURE__*/_jsx(BreezeLoadingDot, {}), unescapedText('aiAddonCopilotPreview.components.copilotPreviewInner.generatingSummary')]
      })]
    });
  }
  if (isDisabled) {
    var _overrideDisabled$dis, _overrideDisabled$dis2;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderHeader(), /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        gap: "200",
        marginBlockEnd: "300",
        children: /*#__PURE__*/_jsx(Tooltip, {
          delay: BREEZE_TOOLTIP_DELAY,
          title: unescapedText('aiAddonCopilotPreview.components.copilotPreviewInner.disabledTooltip'),
          disabled: Boolean(overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.disabledSubtitle),
          children: /*#__PURE__*/_jsx(Text, {
            as: "small",
            color: "text-core-subtle",
            children: (_overrideDisabled$dis = overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.disabledSubtitle) !== null && _overrideDisabled$dis !== void 0 ? _overrideDisabled$dis : unescapedText('aiAddonCopilotPreview.components.copilotPreviewInner.disabledSubtitle')
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsxs(Small, {
          tagName: "div",
          "data-test-id": "preview-summary",
          onClick: () => {
            if (!isExpanded) {
              setIsExpanded(true);
              onExpand(true);
            }
          },
          children: [hasResponse && /*#__PURE__*/_jsx(ExpandableText, {
            toggleable: false,
            expanded: isExpanded,
            maxHeight: maxHeight,
            children: response.sections.map(section => /*#__PURE__*/_jsx(SimpleMessageSectionDisabled, {
              section: section
            }, section.id))
          }), !hasResponse && /*#__PURE__*/_jsx(View, {
            marginBlockEnd: "400",
            children: /*#__PURE__*/_jsx(Text, {
              as: "small",
              children: (_overrideDisabled$dis2 = overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.disabledDescription) !== null && _overrideDisabled$dis2 !== void 0 ? _overrideDisabled$dis2 : unescapedText('aiAddonCopilotPreview.components.copilotPreviewInner.disabledDescription')
            })
          }), isStreaming && /*#__PURE__*/_jsx(BreezeLoadingDot, {})]
        })
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderHeader(), /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "200",
      marginBlockEnd: "300",
      children: [/*#__PURE__*/_jsx(SummaryTimestamp, {
        timestamp: response && response.timestamp
      }), !isLoading && !isStreaming && /*#__PURE__*/_jsx(BreezeRefreshButton, {
        onRefresh: () => {
          onRefresh();
          setIsExpanded(true);
        }
      })]
    }), /*#__PURE__*/_jsxs(View, {
      marginBlockEnd: "200",
      onClick: () => {
        if (!isExpanded) {
          setIsExpanded(true);
          onExpand(true);
        }
      },
      cursor: isExpanded ? 'default' : 'pointer',
      children: [/*#__PURE__*/_jsx(ExpandableText, {
        toggleable: false,
        expanded: isExpanded,
        maxHeight: maxHeight,
        children: /*#__PURE__*/_jsxs(Small, {
          tagName: "div",
          "data-test-id": "preview-summary",
          children: [hasResponse && response.sections.map(section => /*#__PURE__*/_jsx(SimpleMessageSection, {
            section: section,
            onQuickActionClick: onQuickActionClick,
            usageTracker: usageTracker
          }, section.id)), isStreaming && /*#__PURE__*/_jsx(BreezeLoadingDot, {})]
        })
      }), children && shouldShowActions && /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        children: children
      })]
    }), isExpanded && hasResponse && response.messageId && /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      marginBlockEnd: "400",
      children: /*#__PURE__*/_jsx(InlineFeedback, {
        copyMessage: copyMessage,
        messageId: response.messageId,
        onPositiveFeedback: onPositiveFeedback,
        onNegativeFeedback: onNegativeFeedback,
        usageTracker: usageTracker,
        usageTrackerAppLocationKey: usageTrackerAppLocationKey
      })
    })]
  });
};
export default CopilotPreviewInnerExpandable;